/**
 * useKanbanDesign — the runtime Kanban+Cards A/B switch (master spec R1 §1.3).
 *
 * The design-lightening layer as a whole is a BUILD flag (VITE_DESIGN_LIGHT).
 * Inside it, the Kanban board + card skin is additionally flippable at RUNTIME
 * between the new lighter treatment and the exact-current v2 markup — no
 * rebuild — via the html[data-kanban-ab='new'|'old'] attribute (set pre-render
 * in main.tsx from localStorage). A discreet on-board control (Agent B) flips
 * the attribute and dispatches the 'chaos:kanban-ab' CustomEvent; a second open
 * tab syncs via the native 'storage' event.
 *
 * Contract for consumers (Agent B):
 *   const design = useKanbanDesign();            // 'new' | 'old' | 'off'
 *   design === 'new'  → render the NEW lighter markup (card meta line, etc.)
 *   design === 'old'  → render TODAY's exact deployed v2 markup (untouched)
 *   design === 'off'  → VITE_DESIGN_LIGHT is false; render TODAY's v2 markup.
 * i.e. BOTH 'old' and 'off' take the current-markup branch — only 'new' opts in.
 *
 * OFF-build guarantee: when VITE_DESIGN_LIGHT is false the hook always returns
 * 'off' (the runtime attribute is never set by main.tsx either), so the 'new'
 * branch is never taken and the OFF build renders byte-identical to today.
 */
import * as React from 'react';

import { env } from '../../env';

/** localStorage key holding the user's runtime Kanban A/B choice. */
export const KANBAN_AB_STORAGE_KEY = 'chaos.design.kanbanAb';

/** CustomEvent dispatched on `window` when the A/B choice changes in-tab. */
export const KANBAN_AB_EVENT = 'chaos:kanban-ab';

/**
 * Read the persisted Kanban A/B choice. Default 'new'. Best-effort — any
 * localStorage failure (private mode / SSR) degrades to 'new'. Exported for
 * main.tsx's pre-render bootstrap so the storage key + defaulting rule live in
 * exactly one place.
 */
export function readKanbanAb(): 'new' | 'old' {
  // A/B RETIRED 2026-07-12: the founder approved the lighter skin as THE Kanban
  // design and asked for the flask toggle to be removed, so the board is always
  // 'new' whenever VITE_DESIGN_LIGHT is on. We intentionally IGNORE any stored
  // 'old' (a value some testers left in localStorage) so nobody is stranded on
  // the classic skin with no control to flip back. `setKanbanAb` + the event
  // machinery are kept intact — re-enabling the A/B is a one-line change here
  // plus re-adding a control — but nothing calls setKanbanAb now. OFF-build
  // parity is unchanged: `useKanbanDesign` still returns 'off' when the build
  // flag is false, so a VITE_DESIGN_LIGHT=false rebuild is byte-identical.
  return 'new';
}

/**
 * Persist + broadcast a new Kanban A/B choice. Sets the runtime attribute,
 * writes localStorage, and dispatches the in-tab CustomEvent (cross-tab sync is
 * handled by the native 'storage' event on the OTHER tab). Exported for Agent
 * B's flask control + the Settings mirror.
 */
export function setKanbanAb(next: 'new' | 'old'): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(KANBAN_AB_STORAGE_KEY, next);
    }
  } catch {
    /* best-effort — persistence never throws into the click handler */
  }
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.kanbanAb = next;
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(KANBAN_AB_EVENT));
  }
}

/**
 * Subscribe to the runtime Kanban A/B choice. Returns 'off' when the build flag
 * VITE_DESIGN_LIGHT is false. See the module header for the consumer contract.
 */
export function useKanbanDesign(): 'new' | 'old' | 'off' {
  const [ab, setAb] = React.useState<'new' | 'old'>(() => readKanbanAb());

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const sync = (): void => setAb(readKanbanAb());
    const onStorage = (e: StorageEvent): void => {
      if (e.key === KANBAN_AB_STORAGE_KEY) sync();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(KANBAN_AB_EVENT, sync as EventListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(KANBAN_AB_EVENT, sync as EventListener);
    };
  }, []);

  return env.VITE_DESIGN_LIGHT ? ab : 'off';
}
